import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import React, {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import VehicleDiagLoadingKr from '@di-shared/features/home/kr/vehicle/VehicleDiagLoading.kr';
import VehicleTireKr from '@di-shared/features/home/kr/vehicle/VehicleTire.kr';
import VehicleBatteryKr from '@di-shared/features/home/kr/vehicle/VehicleBattery.kr';
import VehicleBrakePadKr from '@di-shared/features/home/kr/vehicle/VehicleBrakePad.kr';
import VehicleSestKr from '@di-shared/features/home/kr/vehicle/VehicleSest.kr';
import VehicleCertificationGradeKr from '@di-shared/features/home/kr/vehicle/VehicleCertificationGrade.kr';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {PermissionTypeKr} from '@di-shared/api/di/type';
import RenderIfTireDataKr from '@di-shared/features/home/kr/vehicle/RenderIfTireData.kr';
import RenderIfBatteryDataKr from '@di-shared/features/home/kr/vehicle/RenderIfBatteryData.kr';
import RenderIfBrakePadDataKr from '@di-shared/features/home/kr/vehicle/RenderIfBrakePadData.kr';
import ParticipatoryEvBatteryCare from '@di-shared/features/home/kr/ev-battery/participatory-care/ParticipatoryEvBatteryCare';
import EvBatteryMonitor from '@di-shared/features/home/kr/ev-battery/monitor/EVBatteryMonitor';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import {TabType} from '@di-shared/features/home/kr/HomeContents.kr';

export interface VehicleContentsKrProps {
  vin: string;
  puid: string;
  ccid: string;
}

enum TileIndex {
  TIRE,
  BATTERY,
  BRAKE,
  CPO,
}

function VehicleContentsKr({vin, puid, ccid}: VehicleContentsKrProps) {
  const {t} = useInferableTranslation();
  const {checkGlobalVinWhitelist} = useFirebaseStore(({checkGlobalVinWhitelist}) => ({checkGlobalVinWhitelist}));
  const {checkAvailableComponent, isAgreeFeature} = useAvailableComponentKr({vin}); // suspense
  const [tileVisibility, setTileVisibility] = useState<boolean[]>([
    !!checkAvailableComponent(PermissionTypeKr.TIRE),
    !!checkAvailableComponent(PermissionTypeKr.BATTERY_LOW),
    !!checkAvailableComponent(PermissionTypeKr.BRAKE),
    !!checkAvailableComponent(PermissionTypeKr.CPO),
  ]);
  const numberOfTilesBeforeVehicleCertificationGrade = tileVisibility.slice(0, 3).filter(Boolean).length;
  const isVehicleCertificationGradeExtendeded =
    numberOfTilesBeforeVehicleCertificationGrade === 0 || numberOfTilesBeforeVehicleCertificationGrade === 2;

  function changeTileInvisible(index: TileIndex) {
    setTileVisibility((prevTiles) => {
      const updatedTiles = [...prevTiles];
      updatedTiles[index] = false;
      return updatedTiles;
    });
  }

  /**
   * [2024-11-28:Jeyoung Lee] FIXME:
   * error 컴포넌트에서 LinkTo -> customalert로 바꿔야함
   * 테스트도 한번씩 해봐야됨
   *
   * fixme:
   * 브레이크패드의 경우 error component랑 update component가 달랐음 -> 아직 협의 중
   * @link https://hmg.dooray.com/project/tasks/3946597057095753039
   */

  return (
    <div className="tab_content car_management">
      <section
        className="section"
        id={`${TabType.Vehicle}_tabpanel`}
        aria-labelledby={`${TabType.Vehicle}_tab`}
      >
        <h2 className="blind">{t('T20050')}</h2>
        {/* 차량진단 */}
        <section className="section section_car_diagnosis">
          <h3 className="blind">{t('T10103')}</h3>
          <ul className="car_diagnosis_list">
            {/* 타이어 공기압 */}
            {checkAvailableComponent(PermissionTypeKr.TIRE) && (
              <ErrorBoundary fallback={<VehicleTireKr.Error />}>
                <Suspense fallback={<VehicleDiagLoadingKr />}>
                  <RenderIfTireDataKr
                    vin={vin}
                    onNoData={() => changeTileInvisible(TileIndex.TIRE)}
                  >
                    <VehicleTireKr vin={vin} />
                  </RenderIfTireDataKr>
                </Suspense>
              </ErrorBoundary>
            )}
            {/* 12V 배터리 */}
            {checkAvailableComponent(PermissionTypeKr.BATTERY_LOW) && (
              <ErrorBoundary fallback={<VehicleBatteryKr.Error />}>
                <Suspense fallback={<VehicleDiagLoadingKr />}>
                  <RenderIfBatteryDataKr
                    vin={vin}
                    onNoData={() => changeTileInvisible(TileIndex.BATTERY)}
                  >
                    <VehicleBatteryKr vin={vin} />
                  </RenderIfBatteryDataKr>
                </Suspense>
              </ErrorBoundary>
            )}
            {/* 브레이크 패드 */}
            {checkAvailableComponent(PermissionTypeKr.BRAKE) && (
              <ErrorBoundary fallback={<VehicleBrakePadKr.Error />}>
                <Suspense fallback={<VehicleDiagLoadingKr />}>
                  <RenderIfBrakePadDataKr
                    vin={vin}
                    onNoData={() => changeTileInvisible(TileIndex.BRAKE)}
                  >
                    <VehicleBrakePadKr vin={vin} />
                  </RenderIfBrakePadDataKr>
                </Suspense>
              </ErrorBoundary>
            )}

            {/* 차량 인증 등급 (현대/제네시스) */}
            {checkAvailableComponent(PermissionTypeKr.CPO) && (
              <VehicleCertificationGradeKr
                vin={vin}
                isExtended={isVehicleCertificationGradeExtendeded}
              />
            )}
          </ul>
        </section>
        {/* EV배터리 공통 */}
        {checkAvailableComponent(PermissionTypeKr.BATTERY_PFRM_MNG_DI_SERVICE) &&
          isAgreeFeature(PermissionTypeKr.BATTERY_PFRM_MNG_DI_SERVICE) && (
            <EvBatteryMonitor
              vin={vin}
              puid={puid}
            />
          )}
        {/*참여형 EV 배터리 케어 현대캐피탈 특화 정보 */}
        {checkAvailableComponent(PermissionTypeKr.BATTERY_PFRM_MNG_SERVICE) &&
          isAgreeFeature(PermissionTypeKr.BATTERY_PFRM_MNG_SERVICE) && (
            <ParticipatoryEvBatteryCare
              vin={vin}
              puid={puid}
            />
          )}

        {/* 소모품 */}
        {checkAvailableComponent(PermissionTypeKr.CONSUMABLES) && (
          <VehicleSestKr
            vin={vin}
            ccid={ccid}
          />
        )}
      </section>
    </div>
  );
}

export default VehicleContentsKr;
